import { Box, type Breakpoint, Paper, Typography, useTheme } from "@mui/material";
import React from "react";

export interface PageHeaderProps {
  title: string;
  headerActions?: React.ReactNode;
  gradientTitle?: boolean;
  icon?: React.ReactNode; // Icon to display to the left of the title
  titleAccessory?: React.ReactNode;
  stackBelowBreakpoint?: Breakpoint;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  headerActions,
  gradientTitle,
  icon,
  titleAccessory,
  stackBelowBreakpoint = "md",
}) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const stackedHeaderSx = {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 1.5,
  } as const;
  const inlineHeaderSx = {
    flexDirection: "row",
    alignItems: "center",
    gap: 0,
  } as const;
  const titleGradient = isRtl
    ? "/* @noflip */ linear-gradient(256deg, #06F 3.05%, #00B7FF 96.92%)"
    : "/* @noflip */ linear-gradient(104deg, #06F 3.05%, #00B7FF 96.92%)";
  const renderIcon = (node: React.ReactNode) => {
    if (!node) return null;
    if (React.isValidElement(node)) {
      // Keep common MUI icons exactly aligned with the title's inherited font size and color.
      const existingSx = (node as any).props?.sx;
      return React.cloneElement(node as any, {
        fontSize: "inherit",
        color: "inherit",
        sx: {
          color: "inherit",
          display: "inline-flex",
          alignItems: "center",
          ...(existingSx || {}),
          fontSize: "1em",
        },
      });
    }
    return node;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 0,
        mb: { xs: 3, md: 2 },
        py: 1,
        display: "flex",
        // Match one default Autocomplete (56px), the header's vertical padding,
        // and its 1px bottom border.
        // Wrapped controls can still expand the header at narrower widths.
        minHeight: 73,
        justifyContent: "space-between",
        flexWrap: "wrap",
        ...stackedHeaderSx,
        borderBottom: "1px solid",
        borderColor: "divider",
        borderRadius: 0,
        bgcolor: "transparent",
        [theme.breakpoints.up(stackBelowBreakpoint)]: inlineHeaderSx,
      }}
    >
      <Typography
        variant="h5"
        component="h1"
        sx={{
          fontWeight: 800,
          color: gradientTitle ? "transparent" : "text.primary",
          fontSize: { xs: "1.4rem", md: "2rem" },
          lineHeight: { xs: 1.2, sm: 1.4 },
          my: 0,
          textAlign: "start",
          display: { xs: "flex", md: "block" },
          alignItems: "center",
          ...(gradientTitle
            ? {
                background: titleGradient,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: { xs: "flex", md: "inline-block" },
              }
            : {}),
        }}
      >
        <Box
          component="span"
          sx={{
            display: { xs: "flex", md: "inline-flex" },
            alignItems: "center",
            flexDirection: "row",
            gap: 1,
            color: "inherit",
            fontSize: "1em",
          }}
        >
          {icon && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: "1em",
                transform: { xs: "translateY(0.125em)", sm: "none" },
                // Make the icon inherit the same gradient as the title when gradientTitle is enabled
                ...(gradientTitle
                  ? {
                      color: "#0066ff",
                      background: titleGradient,
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }
                  : { color: "text.primary" }),
              }}
            >
              {renderIcon(icon)}
            </Box>
          )}
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 1,
            }}
          >
            <Box component="span">{title}</Box>
            {titleAccessory && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  alignSelf: "baseline",
                  color: "inherit",
                  lineHeight: 1,
                }}
              >
                {titleAccessory}
              </Box>
            )}
          </Box>
        </Box>
      </Typography>
      {headerActions && (
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.75, sm: 1 },
            width: "100%",
            justifyContent: "flex-start",
            alignSelf: "flex-start",
            flexWrap: "wrap",
            [theme.breakpoints.up(stackBelowBreakpoint)]: {
              width: "auto",
              justifyContent: "flex-end",
              alignSelf: "auto",
              flex: "0 0 auto",
              flexWrap: "nowrap",
              maxWidth: "100%",
            },
          }}
        >
          {headerActions}
        </Box>
      )}
    </Paper>
  );
};
